import React, { useCallback } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';

import {
  EQuantityFormatKey,
  ETimeBaseUnit,
  IStoreSettingsFormValues
} from '../../../StoreSettings.types';
import {
  ABBREVIATION_RADIO_CLASSNAME,
  ABBREVIATIONS_V2_TEST_IDS,
  ABBREVIATIONS_V2_TEXTS,
  MIN_BASE_UNIT_THRESHOLD,
  TIME_BASE_UNIT_OPTIONS,
  getTimeBaseUnitTestId
} from '../AbbreviationsSettingsV2.consts';
import {
  SettingsFieldPath,
  SettingsFieldValue
} from '../AbbreviationsSettingsV2.types';
import { getTimeFieldPath } from '../AbbreviationsSettingsV2.utils';

import { SettingsTile } from './SettingsTile';

interface IBaseUnitFieldProps {
  sectionKey: EQuantityFormatKey;
  onCommit: (path: SettingsFieldPath, value: SettingsFieldValue) => void;
}

export const BaseUnitField = ({
  sectionKey,
  onCommit
}: IBaseUnitFieldProps): React.ReactElement => {
  const { control } = useFormContext<IStoreSettingsFormValues>();
  const thresholdPath = getTimeFieldPath(sectionKey, 'baseUnitThreshold');
  const baseUnitPath = getTimeFieldPath(sectionKey, 'baseUnit');
  const threshold = useWatch({ control, name: thresholdPath });
  const baseUnit = useWatch({ control, name: baseUnitPath });

  const handleThresholdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      onCommit(thresholdPath, Number(event.target.value));
    },
    [onCommit, thresholdPath]
  );

  const handleUnitChange = useCallback(
    (value: string): void => {
      onCommit(baseUnitPath, value as ETimeBaseUnit);
    },
    [onCommit, baseUnitPath]
  );

  return (
    <SettingsTile
      title={ABBREVIATIONS_V2_TEXTS.BASE_UNIT_LABEL}
      tooltip={ABBREVIATIONS_V2_TEXTS.BASE_UNIT_TOOLTIP}
      testId={ABBREVIATIONS_V2_TEST_IDS.baseUnitTile}
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-base-foreground">
          {ABBREVIATIONS_V2_TEXTS.THRESHOLD_LABEL}
        </span>
        <Input
          type="number"
          min={MIN_BASE_UNIT_THRESHOLD}
          value={String(threshold)}
          onChange={handleThresholdChange}
          className="w-[262px]"
          aria-label={ABBREVIATIONS_V2_TEXTS.THRESHOLD_LABEL}
          data-testid={ABBREVIATIONS_V2_TEST_IDS.thresholdInput}
        />
      </div>
      <div className="h-px w-full bg-base-border" />
      <RadioGroup
        value={String(baseUnit)}
        onValueChange={handleUnitChange}
        className="flex flex-wrap gap-6"
      >
        {TIME_BASE_UNIT_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2"
          >
            <RadioGroupItem
              value={option.value}
              className={ABBREVIATION_RADIO_CLASSNAME}
              data-testid={getTimeBaseUnitTestId(option.value)}
            />
            <span className="text-sm font-medium text-base-foreground">
              {option.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </SettingsTile>
  );
};
